/**
 * The weekly plan's numbers: one week of planned routes, priced per day and grouped by category.
 *
 * A week is built either live from the plan (buildLiveWeekData) or from the snapshot taken when the
 * week was locked (snapshotToWeekData). Both produce the same WeekData shape, so the screens do not
 * care which one they are showing.
 */
import { format, isAfter, isBefore, startOfDay } from "date-fns"

import type { WeeklyPlanSnapshotData } from "@/hooks/useCollections"
import { DAY_NAMES } from "@/lib/collectionCoverage"
import {
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  calculateDurationMinutes,
  findVehicleType,
  priceRoute,
  resolveRouteKind,
} from "@/lib/routeCostCalculation"
import {
  CollectionsConfig,
  ROUTE_CATEGORIES,
  RouteCostBreakdown,
  RoutePlanned,
  RouteStop,
  VehicleType,
} from "@/types/collections"

export interface RouteDayDetail {
  labourAndCharging: number
  /** Net cost for display: labour + charging - haulage revenue */
  netCost?: number
  active: boolean
  costBreakdown?: RouteCostBreakdown | null
  distanceMiles?: number | null
  durationMinutes?: number | null
  vehicleType?: string
  startTime?: string
  endTime?: string
  stops?: RouteStop[]
}

export interface RouteWeeklyData {
  routeId: string
  code: string
  category: string
  status: string
  /** Keyed by yyyy-MM-dd, one entry per day of the week. */
  dayCosts: Record<string, RouteDayDetail>
  weekCost: number
}

export interface CategoryData {
  name: string
  routes: RouteWeeklyData[]
}

export interface DaySummary {
  routeCount: number
  labourCost: number
  chargingCost: number
  vehicleCost: number
  haulageRevenue: number
  totalCost: number
}

export interface WeekData {
  categories: CategoryData[]
  /** Keyed by yyyy-MM-dd, one entry per day of the week. */
  dailySummaries: Record<string, DaySummary>
  weekTotals: DaySummary
  isLocked: boolean
}

export type LiveWeekData = Omit<WeekData, "isLocked">

const CATEGORY_ORDER: string[] = [...ROUTE_CATEGORIES]

const dateKey = (d: Date) => format(d, "yyyy-MM-dd")

/** The stored category, or, for a route without one, a guess from code, operator and vehicle. */
export function getRouteCategory(route: RoutePlanned): string {
  if (route.category) return route.category
  const code = (route.code || "").toUpperCase()
  const operator = (route.operator || "").toUpperCase()
  const vehicleType = (route.vehicle_type || "").toUpperCase()
  const hasHGV = vehicleType.includes("HGV") || code.includes("HGV")
  if (code.includes("PUDO")) return "PUDO"
  if (code.includes("HAULAGE") || code.startsWith("HAUL")) return "Haulage"
  if (code.includes("TRUNK") || code.includes("EAST-WEST-MAGNUM") || code.includes("TRUNKING"))
    return "Trunking"
  if (operator === "OAKLANDS" || code.startsWith("OAK-")) return "HGV Oakland"
  if (hasHGV) return "HGV"
  if (code.startsWith("LDN-")) return "HIVEDone"
  if (["SO-", "RN-", "BR-"].some((p) => code.startsWith(p))) return "South Coast"
  return "Other"
}

/**
 * Known categories first, in ROUTE_CATEGORIES order, then any others in the order first seen.
 * Routes within a category are sorted by code. Sorts the routes arrays in place.
 */
export function groupByCategory(routes: RouteWeeklyData[]): CategoryData[] {
  const catMap = new Map<string, RouteWeeklyData[]>()
  routes.forEach((d) => {
    const arr = catMap.get(d.category) || []
    arr.push(d)
    catMap.set(d.category, arr)
  })
  const categories: CategoryData[] = []
  for (const name of CATEGORY_ORDER) {
    const catRoutes = catMap.get(name)
    if (catRoutes && catRoutes.length > 0) {
      catRoutes.sort((a, b) => a.code.localeCompare(b.code))
      categories.push({ name, routes: catRoutes })
    }
  }
  catMap.forEach((catRoutes, name) => {
    if (!CATEGORY_ORDER.includes(name) && catRoutes.length > 0) {
      catRoutes.sort((a, b) => a.code.localeCompare(b.code))
      categories.push({ name, routes: catRoutes })
    }
  })
  return categories
}

export function emptyDaySummary(): DaySummary {
  return {
    routeCount: 0,
    labourCost: 0,
    chargingCost: 0,
    vehicleCost: 0,
    haulageRevenue: 0,
    totalCost: 0,
  }
}

export interface LiveWeekInputs {
  routes: RoutePlanned[] | undefined
  vehicleTypes: VehicleType[] | undefined
  config: CollectionsConfig | undefined
  weekStart: Date
  weekEnd: Date
  /** The seven days Monday to Sunday, in order, so index i is DAY_NAMES[i]. */
  weekDays: Date[]
}

/**
 * Prices every live route that runs in the week from its plan. Until routes, vehicle types and
 * config have all loaded, the week is empty: no categories and zeroed days.
 */
export function buildLiveWeekData({
  routes,
  vehicleTypes,
  config,
  weekStart,
  weekEnd,
  weekDays,
}: LiveWeekInputs): LiveWeekData {
  const summaries: Record<string, DaySummary> = {}
  weekDays.forEach((d) => {
    summaries[dateKey(d)] = emptyDaySummary()
  })

  if (!routes || !vehicleTypes || !config) {
    return { categories: [], dailySummaries: summaries, weekTotals: emptyDaySummary() }
  }

  // Whole-fleet cost per day: one monthly figure on config, divided by 30.
  const dailyVehicleCostTotal = (config.fleet_cost_per_month_gbp ?? 0) / 30

  const activeRoutes = routes.filter((route) => {
    if (route.status !== "live") return false
    const effectiveFrom = startOfDay(new Date(route.effective_from))
    if (isAfter(effectiveFrom, weekEnd)) return false
    if (route.effective_to) {
      const effectiveTo = startOfDay(new Date(route.effective_to))
      if (isBefore(effectiveTo, weekStart)) return false
    }
    return route.days?.some((d) => DAY_NAMES.includes(d))
  })

  const routeDataList: RouteWeeklyData[] = activeRoutes.map((route) => {
    const category = getRouteCategory(route)
    const dayCosts: Record<string, RouteDayDetail> = {}
    let weekCost = 0

    weekDays.forEach((day, index) => {
      const dayName = DAY_NAMES[index]
      const dateStr = dateKey(day)
      const inactive: RouteDayDetail = { labourAndCharging: 0, active: false }

      if (!route.days?.includes(dayName)) {
        dayCosts[dateStr] = inactive
        return
      }
      const effectiveFrom = startOfDay(new Date(route.effective_from))
      if (isBefore(day, effectiveFrom)) {
        dayCosts[dateStr] = inactive
        return
      }
      if (route.effective_to) {
        const effectiveTo = startOfDay(new Date(route.effective_to))
        if (isAfter(day, effectiveTo)) {
          dayCosts[dateStr] = inactive
          return
        }
      }
      if (route.excluded_dates?.some((d) => d.split("T")[0] === dateStr)) {
        dayCosts[dateStr] = inactive
        return
      }

      // No snapshot: always use planned data for costings (distance, duration, revenue) so figures
      // match the plan
      let distanceMiles: number | null = null
      if (route.stops && route.stops.length >= 2) {
        const sorted = [...route.stops]
          .sort((a, b) => a.sequence - b.sequence)
          .map((s) => ({ lat: s.location?.latitude || 0, lng: s.location?.longitude || 0 }))
        distanceMiles = applyPlannedMileageMultiplier(
          calculateDistanceFromPlannedStops(sorted),
          config,
        )
      }

      const durationMinutes = calculateDurationMinutes(
        new Date(`${dateStr}T${route.default_start_time}`),
        new Date(`${dateStr}T${route.default_end_time}`),
      )

      // As in the weekly snapshot: a route whose vehicle type cannot be resolved is left out,
      // except a stand-by day, which has none by design and is priced on hours.
      if (
        !findVehicleType(vehicleTypes, route.vehicle_type) &&
        resolveRouteKind(route.route_kind) !== "standby"
      ) {
        dayCosts[dateStr] = inactive
        return
      }

      // Vehicle cost is not attributed per route; weekly plan uses on-road fleet total (cost/30 per
      // day) in summary
      const cost = priceRoute({
        vehicleTypes,
        vehicleTypeRef: route.vehicle_type,
        config,
        distanceMiles,
        durationMinutes,
        plan: { stops: route.stops, crewSize: route.crew_size, routeKind: route.route_kind },
        // exclude from route cost; vehicle cost comes from on-road list total
        monthlyLeaseCostOverride: 0,
      })

      if (cost) {
        const lc = cost.driver_wage_cost + cost.charging_cost
        const revenue = cost.total_haulage_delivery_revenue ?? 0
        const net = lc - revenue
        dayCosts[dateStr] = {
          labourAndCharging: lc,
          netCost: net,
          active: true,
          costBreakdown: cost,
          distanceMiles,
          durationMinutes,
          vehicleType: route.vehicle_type || undefined,
          startTime: route.default_start_time,
          endTime: route.default_end_time,
          stops: route.stops,
        }
        weekCost += net
        summaries[dateStr].routeCount++
        summaries[dateStr].labourCost += cost.driver_wage_cost
        summaries[dateStr].chargingCost += cost.charging_cost
        summaries[dateStr].haulageRevenue += cost.total_haulage_delivery_revenue
        summaries[dateStr].totalCost += cost.driver_wage_cost + cost.charging_cost
      } else {
        dayCosts[dateStr] = {
          labourAndCharging: 0,
          active: true,
          stops: route.stops,
          startTime: route.default_start_time,
          endTime: route.default_end_time,
        }
      }
    })

    return {
      routeId: route.id,
      code: route.code,
      category,
      status: route.status,
      dayCosts,
      weekCost,
    }
  })

  weekDays.forEach((day) => {
    const dateStr = dateKey(day)
    summaries[dateStr].vehicleCost = dailyVehicleCostTotal
    summaries[dateStr].totalCost += dailyVehicleCostTotal
  })

  const weekTotals = Object.values(summaries).reduce(
    (acc, d) => ({
      routeCount: acc.routeCount + d.routeCount,
      labourCost: acc.labourCost + d.labourCost,
      chargingCost: acc.chargingCost + d.chargingCost,
      vehicleCost: acc.vehicleCost + d.vehicleCost,
      haulageRevenue: acc.haulageRevenue + d.haulageRevenue,
      totalCost: acc.totalCost + d.totalCost,
    }),
    emptyDaySummary(),
  )

  return { categories: groupByCategory(routeDataList), dailySummaries: summaries, weekTotals }
}

/**
 * A locked week, read back from its snapshot. The snapshot keeps costs only, so the days carry no
 * stops, times, distance or duration.
 */
export function snapshotToWeekData(snapshot: WeeklyPlanSnapshotData, weekDays: Date[]): WeekData {
  const dateStrs = weekDays.map(dateKey)

  const dailySummaries: Record<string, DaySummary> = {}
  dateStrs.forEach((ds) => {
    const s = snapshot.daily_summaries[ds]
    dailySummaries[ds] = s
      ? {
          routeCount: s.route_count,
          labourCost: s.labour_cost,
          chargingCost: s.charging_cost,
          vehicleCost: s.vehicle_cost,
          haulageRevenue: s.haulage_revenue,
          totalCost: s.total_cost,
        }
      : emptyDaySummary()
  })

  const wt = snapshot.week_totals
  const weekTotals: DaySummary = {
    routeCount: wt.route_count,
    labourCost: wt.labour_cost,
    chargingCost: wt.charging_cost,
    vehicleCost: wt.vehicle_cost,
    haulageRevenue: wt.haulage_revenue,
    totalCost: wt.total_cost,
  }

  const routeDataList: RouteWeeklyData[] = snapshot.routes.map((r) => {
    const dayCosts: Record<string, RouteDayDetail> = {}
    let weekCost = 0
    dateStrs.forEach((ds) => {
      const dc = r.days[ds]
      if (dc) {
        const lc = dc.labour_cost + dc.charging_cost
        const revenue = dc.haulage_revenue ?? 0
        const net = lc - revenue
        dayCosts[ds] = {
          labourAndCharging: lc,
          netCost: net,
          active: true,
          costBreakdown: {
            driver_wage_cost: dc.labour_cost,
            charging_cost: dc.charging_cost,
            vehicle_lease_cost: dc.vehicle_cost,
            total_cost: dc.total_cost,
            total_haulage_delivery_revenue: dc.haulage_revenue,
          },
          vehicleType: r.vehicle_type,
        }
        weekCost += net
      } else {
        dayCosts[ds] = { labourAndCharging: 0, active: false }
      }
    })
    return {
      routeId: r.route_id,
      code: r.code,
      category: r.category,
      status: r.status,
      dayCosts,
      weekCost,
    }
  })

  return { categories: groupByCategory(routeDataList), dailySummaries, weekTotals, isLocked: true }
}

/** Every route in the categories that is active on at least one of the dates, sorted by code. */
export function routesActiveOn(categories: CategoryData[], dateStrs: string[]): RouteWeeklyData[] {
  const list: RouteWeeklyData[] = []
  for (const cat of categories) {
    for (const r of cat.routes) {
      if (dateStrs.some((ds) => r.dayCosts[ds]?.active)) list.push(r)
    }
  }
  return list.sort((a, b) => a.code.localeCompare(b.code))
}

export interface RouteRevenue {
  route: RouteWeeklyData
  revenue: number
}

/** Each route's haulage revenue summed over the dates, highest first, leaving out zero earners. */
export function revenueByRoute(categories: CategoryData[], dateStrs: string[]): RouteRevenue[] {
  const list: RouteRevenue[] = []
  for (const cat of categories) {
    for (const r of cat.routes) {
      let rev = 0
      dateStrs.forEach((ds) => {
        rev += r.dayCosts[ds]?.costBreakdown?.total_haulage_delivery_revenue ?? 0
      })
      if (rev > 0) list.push({ route: r, revenue: rev })
    }
  }
  return list.sort((a, b) => b.revenue - a.revenue)
}

export interface RevenueDiff {
  code: string
  leftRev: number
  rightRev: number
  delta: number
}

/** Routes, matched by code, whose revenue differs between the two lists; largest change first. */
export function revenueDiffs(left: RouteRevenue[], right: RouteRevenue[]): RevenueDiff[] {
  const leftMap = new Map<string, number>()
  left.forEach(({ route, revenue }) => {
    leftMap.set(route.code, revenue)
  })
  const rightMap = new Map<string, number>()
  right.forEach(({ route, revenue }) => {
    rightMap.set(route.code, revenue)
  })
  const allCodes = new Set([...leftMap.keys(), ...rightMap.keys()])
  const diffs: RevenueDiff[] = []
  allCodes.forEach((code) => {
    const l = leftMap.get(code) ?? 0
    const r = rightMap.get(code) ?? 0
    if (l !== r) diffs.push({ code, leftRev: l, rightRev: r, delta: r - l })
  })
  diffs.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
  return diffs
}

/** Whole pounds for the week table's cells: "£1,235", "-£40", and "£0" for zero or missing. */
export function formatWeekCell(v: number | null | undefined): string {
  if (v == null || v === 0) return "£0"
  const abs = Math.abs(v)
  const str = `£${Math.round(abs).toLocaleString("en-GB")}`
  return v < 0 ? `-${str}` : str
}
